// Recherche universelle inline (header). Cmd+K / Ctrl+K focus le champ.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  flattenUniversalSearchHits,
  groupUniversalSearchHits,
  isUniversalSearchReady,
  resolveUniversalSearchAction,
  shouldOpenSearchHitInNewTab,
  shouldSpaActivateSearchHit,
  universalSearchHref,
  universalSearchPagePath,
  UNIVERSAL_SEARCH_MIN_CHARS,
  UNIVERSAL_SEARCH_PAGE_PATH,
  type UniversalSearchHit,
} from '../../lib/universalSearch';
import { useUniversalSearch } from '../../hooks/queries/useUniversalSearch';
import { SearchHitLink } from '../search/SearchHitLink';

export function DossierSearchPalette() {
  const [query, setQuery] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const onResultsPage = pathname === UNIVERSAL_SEARCH_PAGE_PATH;
  const pageQuery = onResultsPage ? searchParams.get('q') ?? '' : '';
  // Sur la page de résultats, le champ reprend la recherche de l'adresse ; il se vide quand on la quitte.
  useEffect(() => {
    setQuery(pageQuery);
    setPanelOpen(false);
  }, [onResultsPage, pageQuery]);
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  const showPanel = panelOpen && query.trim().length > 0;
  const ready = isUniversalSearchReady(debounced);
  const { data, isFetching } = useUniversalSearch(showPanel ? debounced : '');
  const sections = useMemo(
    () => (ready && showPanel ? groupUniversalSearchHits(data ?? []) : []),
    [data, ready, showPanel],
  );
  const results = useMemo(() => flattenUniversalSearchHits(sections), [sections]);
  // -1 : aucune suggestion choisie, Entrée ouvre la page de résultats.
  const [selectedIdx, setSelectedIdx] = useState(-1);

  useEffect(() => {
    setSelectedIdx(-1);
  }, [debounced]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (document.activeElement === inputRef.current) {
          inputRef.current?.blur();
          setPanelOpen(false);
        } else {
          inputRef.current?.focus();
          inputRef.current?.select();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  function dismiss() {
    setQuery('');
    setPanelOpen(false);
    inputRef.current?.blur();
  }

  function activate(hit: UniversalSearchHit | undefined) {
    if (!hit) return;
    navigate(resolveUniversalSearchAction(hit).to);
    dismiss();
  }

  function openResultsPage() {
    if (!isUniversalSearchReady(query)) return;
    navigate(universalSearchPagePath(query));
    setPanelOpen(false);
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPanelOpen(true);
      setSelectedIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[selectedIdx];
      if (!hit) {
        openResultsPage();
        return;
      }
      if (shouldOpenSearchHitInNewTab(e)) {
        window.open(universalSearchHref(hit), '_blank', 'noopener,noreferrer');
        return;
      }
      activate(hit);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      dismiss();
    }
  }

  let cursor = 0;

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPanelOpen(true);
          }}
          onFocus={() => setPanelOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Rechercher…"
          aria-label="Rechercher"
          autoComplete="off"
          className={cn(
            'w-[clamp(200px,21vw,320px)] h-9 pl-8 pr-11 text-sm rounded-full border bg-white',
            'placeholder:text-neutral-400 text-neutral-900',
            'focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent',
            'border-neutral-300 hover:border-neutral-400 transition-colors',
          )}
        />
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 font-mono text-[11px] leading-none px-[5px] py-0.5 border border-neutral-200 rounded text-neutral-400">
          ⌘K
        </kbd>
      </div>

      {showPanel ? (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-[min(28rem,calc(100vw-2rem))] bg-white rounded-cime shadow-2xl border border-neutral-200 overflow-hidden z-50"
          onMouseDown={(e) => {
            if (shouldSpaActivateSearchHit(e)) e.preventDefault();
          }}
        >
          <div className="max-h-[50vh] overflow-y-auto">
            {!ready ? (
              <p className="text-sm italic text-neutral-400 px-4 py-6 text-center">
                Tapez au moins {UNIVERSAL_SEARCH_MIN_CHARS} caractères.
              </p>
            ) : isFetching && results.length === 0 ? (
              <p className="text-sm text-neutral-400 px-4 py-6 text-center">
                Recherche…
              </p>
            ) : results.length === 0 ? (
              <p className="text-sm italic text-neutral-400 px-4 py-6 text-center">
                Aucun résultat.
              </p>
            ) : (
              sections.map((section) => {
                const start = cursor;
                cursor += section.hits.length;
                return (
                  <div key={section.kind}>
                    <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                      {section.label}
                    </p>
                    <ul>
                      {section.hits.map((hit, i) => {
                        const idx = start + i;
                        return (
                          <li key={`${hit.kind}-${hit.id}`}>
                            <SearchHitLink
                              hit={hit}
                              selected={idx === selectedIdx}
                              onSelect={() => activate(hit)}
                              onHover={() => setSelectedIdx(idx)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
          {ready ? (
            <Link
              to={universalSearchPagePath(query)}
              onClick={(e) => {
                if (!shouldSpaActivateSearchHit(e)) return;
                e.preventDefault();
                openResultsPage();
              }}
              className={cn(
                'flex items-center gap-2 border-t border-neutral-100 px-4 py-2.5 text-[13px] font-medium text-cime-primary transition-colors',
                selectedIdx === -1 ? 'bg-cime-primary/5' : 'hover:bg-neutral-50',
              )}
            >
              <Search className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 truncate">Voir tous les résultats pour « {query.trim()} »</span>
            </Link>
          ) : null}
          <div className="px-4 py-2 text-[10px] text-neutral-400 border-t border-neutral-100 bg-neutral-50 flex flex-wrap items-center gap-3">
            <span><kbd className="font-mono px-1 border border-neutral-200 bg-white rounded">Entrée</kbd> tous les résultats</span>
            <span><kbd className="font-mono px-1 border border-neutral-200 bg-white rounded">↑↓</kbd> choisir une suggestion</span>
            <span><kbd className="font-mono px-1 border border-neutral-200 bg-white rounded">Ctrl+Entrée</kbd> / <kbd className="font-mono px-1 border border-neutral-200 bg-white rounded">Ctrl+clic</kbd> nouvel onglet</span>
            <span><kbd className="font-mono px-1 border border-neutral-200 bg-white rounded">⌘K</kbd></span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
